// src/pages/review/MyReviewsPage.js
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getMyReviews } from "../../api/reviewApi";
import { useAuth } from "../../hooks/useAuth";
import Button from "../../components/common/Button";

function MyReviewsPage() {
  const { user } = useAuth();

  const [reviews, setReviews] = useState([]);
  const [statusFilter, setStatusFilter] = useState("ALL"); // ALL | PENDING | APPROVED | REJECTED
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 20,
    total: 0,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    loadReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, page]);

  async function loadReviews() {
    try {
      setLoading(true);
      setError("");

      const params = {
        page,
        page_size: 20,
        userId: user.user_id
      };
      if (statusFilter !== "ALL") {
        params.status = statusFilter;
      }

      const data = await getMyReviews(params);
      if (!data.success) {
        setError(data.message || "Failed to load your reviews.");
        setReviews([]);
        setPagination({
          page: 1,
          page_size: 20,
          total: 0,
        });
        return;
      }

      setReviews(data.reviews || []);
      setPagination(data.pagination || { page: 1, page_size: 20, total: 0 });
    } catch (err) {
      console.error(err);
      setError("Failed to load your reviews.");
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }

  const formatDateTime = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleString();
  };

  const formatTypeLabel = (type) => {
    if (!type) return "-";
    if (type === "RESTAURANT") return "Restaurant";
    if (type === "HOTEL") return "Hotel";
    if (type === "GYM") return "Gym";
    return type;
  };

  const formatStatusBadge = (status) => {
    if (status === "APPROVED") {
      return (
        <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
          Approved
        </span>
      );
    }
    if (status === "REJECTED") {
      return (
        <span className="inline-flex items-center rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700">
          Rejected
        </span>
      );
    }
    return (
      <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
        Pending
      </span>
    );
  };

  const totalPages = Math.max(
    1,
    Math.ceil((pagination.total || 0) / (pagination.page_size || 20))
  );

  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <div className="space-y-6">
      {/* Header + back to home */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          
          <div className="animate-fadeIn" >
            <h1 className="text-3xl font-bold tracking-tight">My Reviews</h1>
            <p className="text-gray-600 mt-1">
              Track your submitted reviews, their status, and earned income.
            </p>
          </div>
        </div>
        {/* {user && (
          <div className="text-[11px] text-slate-500">
            Logged in as{" "}
            <span className="font-medium text-slate-700">{user.name}</span>
          </div>
        )} */}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        {/* <div className="inline-flex rounded-md border border-slate-200 bg-white p-1 text-xs">
          {["ALL", "PENDING", "APPROVED", "REJECTED"].map((status) => (
            <button
              key={status}
              type="button"
              onClick={() => {
                setStatusFilter(status);
                setPage(1);
              }}
              className={
                "rounded px-3 py-1 " +
                (statusFilter === status
                  ? "bg-slate-900 text-white"
                  : "text-slate-700 hover:bg-slate-100")
              }
            >
              {status === "ALL"
                ? "All"
                : status.charAt(0) + status.slice(1).toLowerCase()}
            </button>
          ))}
        </div> */}

        <div className="flex items-center gap-2 text-[11px] text-slate-500">
          <span>Total: {pagination.total || 0}</span>
          <span>•</span>
          <span>
            Page {page} / {totalPages}
          </span>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div>Loading your reviews...</div>
      ) : reviews.length === 0 ? (
        <div className="rounded-xl border bg-white px-4 py-6 text-xs text-slate-500">
          You have not submitted any reviews yet.
        </div>
      ) : (
        <div className="rounded-xl bg-white border shadow-sm overflow-hidden">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-50 text-left text-[11px] uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Business</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Answers</th>
                <th className="px-3 py-2">Rating</th>
                <th className="px-3 py-2">Review</th>
                <th className="px-3 py-2">Earned</th>
                {/* <th className="px-3 py-2">Status</th> */}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {reviews.map((r) => (
                <tr key={r.id}>
                  <td className="px-3 py-2">{formatDateTime(r.created_at)}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-col">
                      <span className="font-medium text-slate-800">
                        {r.business_name}
                      </span>
                      {/* <span className="text-[11px] text-slate-500">
                        ID: {r.business_id}
                      </span> */}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <span className="uppercase text-[11px] text-slate-700">
                      {formatTypeLabel(r.business_type)}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    A: {r.ans_a ?? "-"} / B: {r.ans_b ?? "-"} / C:{" "}
                    {r.ans_c ?? "-"}
                  </td>
                  <td className="px-3 py-2">{r.rating}★</td>
                  <td className="px-3 py-2 max-w-xs">
                    <div className="line-clamp-3">{r.review_text}</div>
                  </td>
                  <td className="px-3 py-2">
                    € {Number(r.earned_amount || 0).toFixed(2)}
                  </td>
                  {/* <td className="px-3 py-2">{formatStatusBadge(r.status)}</td> */}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-end gap-2 text-xs">
          <Button
            className="px-3 py-1"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={!canPrev}
          >
            Prev
          </Button>
          <Button
            className="px-3 py-1"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={!canNext}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}

export default MyReviewsPage;
